import { useEffect, useState } from "react";
import { CaretRight, PlugsConnected, Plugs, WarningCircle, PencilSimple, Key, ClockCounterClockwise } from "@phosphor-icons/react";
import { api } from "@/lib/apiClient";

const EVT = {
  "machine.online": { icon: PlugsConnected, cls: "text-emerald-400" },
  "machine.offline": { icon: Plugs, cls: "text-zinc-400" },
  "agent.error": { icon: WarningCircle, cls: "text-red-400" },
  "agent.info": { icon: WarningCircle, cls: "text-blue-400" },
  "machine.added": { icon: PencilSimple, cls: "text-blue-400" },
  "machine.renamed": { icon: PencilSimple, cls: "text-blue-400" },
  "token.regenerated": { icon: Key, cls: "text-amber-400" },
};
const fallback = { icon: WarningCircle, cls: "text-zinc-400" };

const ago = (s) => {
  const diff = (Date.now() - new Date(s).getTime()) / 1000;
  if (isNaN(diff)) return "";
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

export function MachineDetailsPanel({ open, onClose, machine, status }) {
  const [events, setEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(false);

  useEffect(() => {
    if (!open || !machine?.org_id || !machine?.machine_id) return;
    let alive = true;
    setLoadingEvents(true);
    api.get(`/orgs/${machine.org_id}/machines/${machine.machine_id}/events`, { params: { limit: 6 } })
      .then((res) => { if (alive) setEvents(res.data.events || []); })
      .catch(() => {})
      .finally(() => { if (alive) setLoadingEvents(false); });
    return () => { alive = false; };
  }, [open, machine?.org_id, machine?.machine_id, status]);

  return (
    <div className={`absolute top-0 right-0 h-full w-72 bg-zinc-950/95 backdrop-blur-md border-l border-white/10 z-40 transition-transform duration-300 overflow-y-auto ${open ? "translate-x-0" : "translate-x-full"}`}>
      <div className="p-5">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-heading font-semibold tracking-tight">Machine details</h3>
          <button onClick={onClose} className="text-zinc-500 hover:text-white" data-testid="close-panel">
            <CaretRight size={18} />
          </button>
        </div>
        <dl className="space-y-4">
          {[
            ["Name", machine?.name],
            ["Status", status === "online" ? "Online" : "Offline"],
            ["OS", machine?.os || "N/A"],
            ["Hostname", machine?.hostname || "N/A"],
            ["IP address", machine?.ip || "N/A"],
            ["Machine ID", machine?.machine_id],
          ].map(([k, v]) => (
            <div key={k}>
              <dt className="font-mono text-xs uppercase tracking-wider text-zinc-500">{k}</dt>
              <dd className="font-mono text-sm text-zinc-200 mt-1 break-all">{v}</dd>
            </div>
          ))}
        </dl>

        <div className="mt-7 pt-5 border-t border-white/10" data-testid="machine-recent-events">
          <div className="flex items-center gap-2 mb-3 font-mono text-xs uppercase tracking-wider text-zinc-500">
            <ClockCounterClockwise size={14} /> Recent events
          </div>
          {loadingEvents ? (
            <p className="text-xs text-zinc-600 animate-pulse">Loading…</p>
          ) : events.length === 0 ? (
            <p className="text-xs text-zinc-600">No events yet.</p>
          ) : (
            <ul className="space-y-2.5">
              {events.map((e) => {
                const m = EVT[e.type] || fallback;
                const Icon = m.icon;
                return (
                  <li key={e.event_id} className="flex items-start gap-2.5">
                    <Icon size={15} weight="fill" className={`${m.cls} mt-0.5 shrink-0`} />
                    <div className="min-w-0">
                      <p className="text-xs text-zinc-300 leading-snug">{e.message}</p>
                      <p className="text-[10px] text-zinc-600">{ago(e.ts)}{e.actor_email ? ` · ${e.actor_email}` : ""}</p>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
